import { useEffect, useState, useRef } from 'react';
import { Camera, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { updateProfile, uploadProfilePicture } from '@/lib/api';
import { toast } from 'sonner';

export interface SettingsModalProps {
  open: boolean;
  onClose: () => void;
}

export function SettingsModal({ open, onClose }: SettingsModalProps) {
  const { user, refreshUser } = useAuth();
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open && user) {
      setName(user.name ?? user.email ?? '');
      setPhotoPreview(user.picture_url ?? null);
      setPhotoFile(null);
    }
  }, [user, open]);

  useEffect(() => {
    if (!open && photoPreview?.startsWith('blob:')) {
      URL.revokeObjectURL(photoPreview);
      setPhotoPreview(null);
      setPhotoFile(null);
    }
  }, [open, photoPreview]);

  useEffect(() => {
    if (!open) return;
    const onEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onEscape);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose a JPEG, PNG, or WebP image.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be 5 MB or less.');
      return;
    }
    if (photoPreview?.startsWith('blob:')) URL.revokeObjectURL(photoPreview);
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
    e.target.value = '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setSaving(true);
    try {
      let pictureUrl: string | undefined;
      if (photoFile) {
        const { url } = await uploadProfilePicture(photoFile);
        pictureUrl = url;
      }
      const updates: { name?: string; picture_url?: string } = {};
      if (name.trim() !== (user.name ?? user.email ?? '')) updates.name = name.trim();
      if (pictureUrl !== undefined) updates.picture_url = pictureUrl;
      if (Object.keys(updates).length > 0) await updateProfile(updates);
      await refreshUser();
      toast.success('Profile updated');
      setPhotoFile(null);
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50" aria-hidden onClick={onClose} />
      <div
        className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-border bg-card p-6 shadow-xl max-h-[90vh] overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-modal-title"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 id="settings-modal-title" className="text-xl font-display font-bold text-foreground">
            Settings
          </h2>
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
        <p className="text-muted-foreground text-sm mb-6">
          Update your personal details and profile photo. Changes apply to your account across all roles.
        </p>

        {user && (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Role (read-only) */}
            <div className="space-y-2">
              <Label className="text-muted-foreground">Role</Label>
              <p className="text-sm font-medium capitalize rounded-lg bg-muted px-3 py-2 w-fit">
                {user.role}
              </p>
            </div>

            {/* Email (read-only) */}
            <div className="space-y-2">
              <Label htmlFor="settings-email" className="text-muted-foreground">Email</Label>
              <Input
                id="settings-email"
                type="email"
                value={user.email}
                readOnly
                disabled
                className="bg-muted"
              />
            </div>

            {/* Display name */}
            <div className="space-y-2">
              <Label htmlFor="settings-name">Display name</Label>
              <Input
                id="settings-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                maxLength={200}
              />
            </div>

            {/* Profile photo */}
            <div className="space-y-2">
              <Label className="text-muted-foreground">Profile photo</Label>
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-full bg-muted overflow-hidden flex items-center justify-center border-2 border-border">
                  {photoPreview ? (
                    <img src={photoPreview} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-2xl font-semibold text-muted-foreground">
                      {user.email?.[0]?.toUpperCase() ?? '?'}
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={handlePhotoChange}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Camera className="w-4 h-4" /> Change photo
                  </Button>
                  <p className="text-xs text-muted-foreground">JPEG, PNG or WebP. Max 5 MB.</p>
                </div>
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button type="submit" disabled={saving} className="gap-2">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Save changes
              </Button>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </form>
        )}
      </div>
    </>
  );
}
